/**
 * Back-to-OptiNetwork-hub button — Stage 2 navigation cleanup.
 *
 * Small reusable affordance dropped near the page header on every
 * OptiNetwork-linked surface (org dashboards, enterprise talent/training/
 * suggestions, governance, map, region/district dashboards) so the
 * advisor can always return to the hub at `/admin/optinetwork`.
 *
 * Internal-naming note: the component lives under
 * `src/components/floo-network/` (Stage 1 legacy directory) to avoid a
 * directory rename in this pass. The user-facing label / route are
 * `OptiNetwork` / `/admin/optinetwork`.
 *
 * No data fetches, no edge-function calls, no mutations — pure
 * navigation affordance built on react-router's `useNavigate`.
 */

import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface BackToOptiNetworkButtonProps {
  /** Override label. Defaults to "Back to OptiNetwork". */
  label?: string;
  /** Override route. Defaults to "/admin/optinetwork". */
  to?: string;
  /** Optional className passthrough for layout tweaks. */
  className?: string;
  /** Override the default test id for page-specific assertions. */
  testId?: string;
  /** Forwarded to the shadcn Button. Defaults to "ghost". */
  variant?: 'default' | 'outline' | 'ghost' | 'secondary' | 'link' | 'destructive';
  /** Forwarded to the shadcn Button. Defaults to "sm". */
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

export function BackToOptiNetworkButton({
  label = 'Back to OptiNetwork',
  to = '/admin/optinetwork',
  className,
  testId = 'back-to-opti-network',
  variant = 'ghost',
  size = 'sm',
}: BackToOptiNetworkButtonProps) {
  const navigate = useNavigate();
  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={() => navigate(to)}
      className={cn('gap-1', className)}
      data-testid={testId}
    >
      <ArrowLeft className="h-3 w-3" />
      {label}
    </Button>
  );
}

export default BackToOptiNetworkButton;
